/**
 * useProvisionalOptimizer
 * 
 * Hook for invoking the autonomous optimizer in provisional mode
 * when jobs are added to a ticket. Detects scope expansion and
 * returns resolution options for the user to choose from.
 */

import { normalizeOptimizerResponse, type IssuePlansLite } from '@/lib/optimizer/normalizeOptimizerResponse';
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

export interface ResolutionAction {
  type: 'reassign' | 'reschedule' | 'defer' | 'notify' | 'absorb';
  ticketId: string;
  payload: Record<string, unknown>;
}

export interface ResolutionOption {
  id: string;
  label: string;
  description: string;
  score: number;
  confidence: number;
  autoApplyEligible: boolean;
  actions: ResolutionAction[];
  impactSummary: {
    ticketsAffected: number;
    minutesShifted: number;
    techsInvolved: string[];
  };
}

export interface BoardIssue {
  id: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  ticketId: string;
  message: string;
  context: Record<string, unknown>;
  detectedAt: string;
}

export interface ProvisionalResult {
  success: boolean;
  mode: string;
  issuesDetected: BoardIssue[];
  resolutionOptions: ResolutionOption[];
  /** Core-100 CORE-BH/BG: per issue id, the corrective planners' suggestion-only menus. */
  issuePlans?: Record<string, IssuePlansLite>;
  appliedOption?: ResolutionOption;
  runId?: string;
}

export interface UseProvisionalOptimizerReturn {
  isLoading: boolean;
  result: ProvisionalResult | null;
  scopeExpanded: boolean;
  options: ResolutionOption[];
  selectedOption: ResolutionOption | null;
  checkScopeExpansion: (ticketId: string, addedMinutes: number) => Promise<ProvisionalResult | null>;
  selectOption: (option: ResolutionOption) => void;
  applySelectedOption: () => Promise<boolean>;
  reset: () => void;
}

export function useProvisionalOptimizer(): UseProvisionalOptimizerReturn {
  const { activeShop } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ProvisionalResult | null>(null);
  const [selectedOption, setSelectedOption] = useState<ResolutionOption | null>(null);

  const issuesArray = Array.isArray(result?.issuesDetected) ? result.issuesDetected : [];
  const scopeExpanded = issuesArray.some(
    issue => issue.type === 'SCOPE_EXPANDED'
  );

  const options = Array.isArray(result?.resolutionOptions) ? result.resolutionOptions : [];

  const checkScopeExpansion = useCallback(async (
    ticketId: string,
    addedMinutes: number
  ): Promise<ProvisionalResult | null> => {
    if (!activeShop?.id) {
      console.warn('[ProvisionalOptimizer] No active shop');
      return null;
    }

    setIsLoading(true);
    setResult(null);
    setSelectedOption(null);

    try {
      const { data, error } = await supabase.functions.invoke('autonomous-optimizer', {
        body: {
          shopId: activeShop.id,
          mode: 'auto_detect',
          trigger: 'provisional_commit',
          ticketId,
          metadata: {
            addedMinutes,
            provisional: true,
          },
        },
      });

      if (error) {
        console.error('Provisional optimizer error:', error);
        toast({
          title: 'Optimizer Error',
          description: 'Failed to check for scheduling impacts',
          variant: 'destructive',
        });
        return null;
      }

      // Core-100: read the optimizer's REAL contract (issues/options; issuesDetected is a count).
      // The old reads were always empty, so a scope expansion was never detected here.
      const normalized = normalizeOptimizerResponse<ResolutionOption>(data);
      const provisionalResult: ProvisionalResult = {
        ...data,
        issuesDetected: normalized.issuesDetected,
        resolutionOptions: normalized.resolutionOptions,
        issuePlans: normalized.issuePlans,
      };
      setResult(provisionalResult);

      // If scope expanded, log for visibility
      if (provisionalResult.issuesDetected.some(i => i.type === 'SCOPE_EXPANDED')) {
        console.log('[Provisional] Scope expansion detected:', provisionalResult);
      }

      return provisionalResult;
    } catch (err) {
      console.error('Provisional optimizer exception:', err);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [activeShop?.id]);

  const selectOption = useCallback((option: ResolutionOption) => {
    setSelectedOption(option);
  }, []);

  const applySelectedOption = useCallback(async (): Promise<boolean> => {
    if (!selectedOption || !result) {
      toast({
        title: 'No option selected',
        description: 'Please select a resolution option first',
        variant: 'destructive',
      });
      return false;
    }

    if (!activeShop?.id) {
      console.warn('[ProvisionalOptimizer] No active shop');
      return false;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('autonomous-optimizer', {
        body: {
          shopId: activeShop.id,
          mode: 'manual',
          trigger: 'provisional_commit',
          optionId: selectedOption.id,
          issueId: (Array.isArray(result?.issuesDetected) ? result.issuesDetected : []).find(i => i.type === 'SCOPE_EXPANDED')?.id,
        },
      });

      if (error) {
        console.error('Apply option error:', error);
        toast({
          title: 'Failed to apply',
          description: 'Could not apply the selected resolution',
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Resolution applied',
        description: selectedOption.label,
      });

      return true;
    } catch (err) {
      console.error('Apply option exception:', err);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [selectedOption, result, activeShop?.id]);

  const reset = useCallback(() => {
    setResult(null);
    setSelectedOption(null);
    setIsLoading(false);
  }, []);

  return {
    isLoading,
    result,
    scopeExpanded,
    options,
    selectedOption,
    checkScopeExpansion,
    selectOption,
    applySelectedOption,
    reset,
  };
}
